/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { isNonEmptyObject } from "./is-non-empty-object"

/**
 * Formats a parsed API value as indented, YAML-like text without JSON
 * punctuation or string quoting.
 *
 * Objects render as `key: value` lines, and array items start with `- `.
 * Nested values are indented by two spaces per level. Objects inside arrays
 * place their first key on the same line as the list marker. Empty objects
 * and arrays render as `{}` and `[]`. Strings are returned without JSON
 * escaping, preserving plain-text error bodies. The later lines of a
 * multi-line string are indented under its key or list marker.
 *
 * @param value - The parsed API value or plain-text body, or `null` if absent.
 * @returns The formatted text, or an empty string for nullish values and
 * empty top-level objects or arrays.
 *
 * @example
 * formatApiValue({ rootHints: ["KING_ROOT_APP"], rootConfidence: 0.95 })
 * // "rootHints:\n  - KING_ROOT_APP\nrootConfidence: 0.95"
 */
export function formatApiValue(value: unknown): string {
    if (value == null) return ""
    return toLines(value).join("\n")
}

/**
 * Converts a value into lines, without applying indentation to the result.
 *
 * Object properties render as `key: value`; array items start with `- `.
 * Non-empty nested objects and arrays are rendered on subsequent lines under
 * their parent key, or inline with the list marker for array items. Scalar
 * values and empty objects or arrays start on the key or marker line; the
 * later lines of a multi-line string are indented one level below it.
 *
 * @param value - The value to format.
 * @returns The formatted lines, or no lines for an empty object or array.
 *
 * @example
 * toLines({ apps: [{ name: "A", version: "1" }] })
 * // ["apps:", "  - name: A", "    version: 1"]
 */
function toLines(value: unknown): string[] {
    if (Array.isArray(value)) {
        return value.flatMap((item) => {
            const [first, ...rest] = isNonEmptyObject(item) ? toLines(item) : leaf(item)
            return [`- ${first}`, ...indent(rest)]
        })
    }
    if (typeof value === "object" && value !== null) {
        return Object.entries(value).flatMap(([key, item]) => {
            if (isNonEmptyObject(item)) return [`${key}:`, ...indent(toLines(item))]
            const [first, ...rest] = leaf(item)
            return [`${key}: ${first}`, ...indent(rest)]
        })
    }
    return [String(value)]
}

/**
 * Converts a scalar or empty object or array into lines.
 *
 * Empty objects and arrays render as `{}` and `[]` so they remain visible
 * when used as a property value or array item. A multi-line string splits at
 * its line breaks, so the caller can indent each line.
 *
 * @param value - A scalar, or an empty object or array.
 * @returns The value's lines; one line unless it is a multi-line string.
 */
function leaf(value: unknown): string[] {
    return (typeof value === "object" ? JSON.stringify(value) : String(value)).split("\n")
}

/**
 * Indents the lines of a nested value by one level.
 *
 * @param lines - The lines to indent.
 * @returns The lines, each prefixed with two spaces.
 */
function indent(lines: string[]): string[] {
    return lines.map((line) => `  ${line}`)
}
